import './Guides.css';

import { useEffect, useMemo, useState } from 'react';
import { IonIcon, IonSearchbar } from '@ionic/react';
import {
  checkmarkCircle,
  ellipseOutline,
  helpCircleOutline,
  playOutline,
  timeOutline,
} from 'ionicons/icons';
import clsx from 'clsx';
import Button from '../../components/Button/Button';
import MarkdownView from '../../components/Manager/GridInput/components/MarkdownView/MarkdownView';
import { useTranslate } from '../../hooks/context/useTranslate';
import { useUserState } from '../../hooks/context/useUserState';
import useLocalStorageKey from '../../hooks/useLocalStorageKey';
import { useGuideContext } from '../../context/GuideContext/GuideContext';
import { GuideSummary, PathDefinition } from '../../types/guides';
import { getGuideProgress } from '../../utils/guides/guideProgressStore';
import {
  guideHubPreferencesStorageKey,
  readGettingStartedVisible,
  writeGettingStartedVisible,
} from '../../utils/guides/guideHubPreferences';

// Hub of Průvodce MySeptim: learning paths (Začínáme), then every agenda guide.
const Guides = () => {
  const { t } = useTranslate();
  const {
    state: { userLogin },
  } = useUserState();
  const preferenceStorageKey =
    guideHubPreferencesStorageKey(useLocalStorageKey());
  const {
    index,
    indexLoading,
    indexError,
    ensureIndex,
    loadPath,
    openPanel,
    startTour,
    progress,
    markProgress,
  } = useGuideContext();
  const [paths, setPaths] = useState<Record<string, PathDefinition>>({});
  const [search, setSearch] = useState('');
  const [gettingStartedVisible, setGettingStartedVisible] = useState(() =>
    readGettingStartedVisible(preferenceStorageKey, userLogin)
  );

  useEffect(() => {
    setGettingStartedVisible(
      readGettingStartedVisible(preferenceStorageKey, userLogin)
    );
  }, [preferenceStorageKey, userLogin]);

  const setGettingStartedPreference = (visible: boolean) => {
    setGettingStartedVisible(visible);
    writeGettingStartedVisible(preferenceStorageKey, userLogin, visible);
  };

  useEffect(() => {
    ensureIndex();
  }, [ensureIndex]);

  useEffect(() => {
    if (!index) return;
    let cancelled = false;
    index.paths.forEach((summary) => {
      loadPath(summary.id).then((path) => {
        if (!cancelled && path)
          setPaths((previous) => ({ ...previous, [summary.id]: path }));
      });
    });
    return () => {
      cancelled = true;
    };
  }, [index, loadPath]);

  const guideTitles = useMemo(() => {
    const map = new Map<string, GuideSummary>();
    index?.guides.forEach((guide) => map.set(guide.id, guide));
    return map;
  }, [index]);

  const groupedGuides = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase();
    const groups = new Map<string, GuideSummary[]>();
    (index?.guides ?? [])
      .filter(
        (guide) =>
          !needle ||
          guide.title.toLocaleLowerCase().includes(needle) ||
          (guide.summary ?? '').toLocaleLowerCase().includes(needle)
      )
      .forEach((guide) => {
        const block = guide.menuBlock ?? '';
        const list = groups.get(block) ?? [];
        list.push(guide);
        groups.set(block, list);
      });
    return Array.from(groups.entries());
  }, [index, search]);

  const stepStatus = (step: { guideId: string; tours: string[] }) => {
    const tourStates = step.tours.map(
      (tourId) => getGuideProgress(progress, 'tour', tourId)?.status
    );
    if (
      step.tours.length > 0 &&
      tourStates.every((status) => status === 'completed')
    )
      return 'completed';
    if (
      tourStates.some(
        (status) => status === 'started' || status === 'completed'
      )
    )
      return 'started';
    if (getGuideProgress(progress, 'guide', step.guideId)) return 'seen';
    return undefined;
  };

  return (
    <div className="guides-page">
      <header className="guides-header">
        <div className="guides-eyebrow">{t('guide.launcher')}</div>
        <h1>{t('guide.title')}</h1>
        <p>{t('guide.intro')}</p>
      </header>

      {indexError && <p className="guides-muted">{t('guide.indexError')}</p>}
      {indexLoading && !index && (
        <p className="guides-muted">{t('guide.loading')}</p>
      )}

      {!gettingStartedVisible && (index?.paths.length ?? 0) > 0 && (
        <button
          type="button"
          className="guides-path-visibility"
          onClick={() => setGettingStartedPreference(true)}
        >
          {t('guide.showGettingStarted')}
        </button>
      )}

      {gettingStartedVisible &&
        index?.paths.map((summary) => {
          const path = paths[summary.id];
          const steps = path?.steps ?? summary.steps;
          const done = steps.filter(
            (step) => stepStatus(step) === 'completed'
          ).length;
          return (
            <section key={summary.id} className="guides-path">
              <div className="guides-path-head">
                <div>
                  <h2>{summary.title}</h2>
                  {summary.summary && (
                    <p className="guides-muted">{summary.summary}</p>
                  )}
                </div>
                <div className="guides-path-actions">
                  <div className="guides-path-progress">
                    {t('guide.pathProgress')} {done} {t('guide.of')}{' '}
                    {steps.length}
                  </div>
                  <button
                    type="button"
                    className="guides-path-visibility"
                    onClick={() => setGettingStartedPreference(false)}
                  >
                    {t('guide.hideGettingStarted')}
                  </button>
                </div>
              </div>
              <ol className="guides-steps">
                {steps.map((step, position) => {
                  const status = stepStatus(step);
                  const guide = guideTitles.get(step.guideId);
                  return (
                    <li
                      key={step.id}
                      className={clsx(
                        'guides-step',
                        status && `guides-step-${status}`
                      )}
                    >
                      <div className="guides-step-marker">
                        {status === 'completed' ? (
                          <IonIcon icon={checkmarkCircle} />
                        ) : (
                          <IonIcon icon={ellipseOutline} />
                        )}
                        <span>{position + 1}</span>
                      </div>
                      <div className="guides-step-body">
                        <h3>{step.title}</h3>
                        {'body' in step && step.body && (
                          <MarkdownView
                            value={step.body}
                            className="guides-markdown"
                          />
                        )}
                        <div className="guides-step-actions">
                          <Button
                            small
                            fill="outline"
                            round={false}
                            onClick={() => {
                              markProgress('path', summary.id, 'started');
                              openPanel(step.guideId);
                            }}
                          >
                            <IonIcon icon={helpCircleOutline} slot="start" />
                            {guide?.title ?? t('guide.help')}
                          </Button>
                          {step.tours.map((tourId) => {
                            const tour = index.tours.find(
                              (item) => item.id === tourId
                            );
                            const tourProgress = getGuideProgress(
                              progress,
                              'tour',
                              tourId
                            );
                            return (
                              <Button
                                key={tourId}
                                small
                                fill="solid"
                                round={false}
                                onClick={() => {
                                  markProgress('path', summary.id, 'started');
                                  startTour(tourId, 'interactive');
                                }}
                              >
                                <IonIcon
                                  icon={
                                    tourProgress?.status === 'completed'
                                      ? checkmarkCircle
                                      : playOutline
                                  }
                                  slot="start"
                                />
                                {tour?.title ?? tourId}
                                {tour?.estimatedMinutes !== undefined && (
                                  <span className="guides-minutes">
                                    <IonIcon icon={timeOutline} />{' '}
                                    {tour.estimatedMinutes} {t('guide.minutes')}
                                  </span>
                                )}
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </section>
          );
        })}

      {index && (
        <section className="guides-all">
          <div className="guides-all-head">
            <h2>{t('guide.allGuides')}</h2>
            <IonSearchbar
              className="guides-search"
              placeholder={t('guide.search')}
              value={search}
              onIonInput={(event) => setSearch(event.detail.value ?? '')}
              debounce={150}
            />
          </div>
          {groupedGuides.length === 0 && (
            <p className="guides-muted">{t('guide.noResults')}</p>
          )}
          {groupedGuides.map(([block, guides]) => (
            <div key={block || 'other'} className="guides-group">
              {block && <h3 className="guides-group-title">{block}</h3>}
              <div className="guides-cards">
                {guides.map((guide) => {
                  const seen = getGuideProgress(progress, 'guide', guide.id);
                  return (
                    <button
                      key={guide.id}
                      type="button"
                      className={clsx(
                        'guides-card',
                        seen && 'guides-card-seen'
                      )}
                      onClick={() => openPanel(guide.id)}
                    >
                      <div className="guides-card-title">
                        {seen && <IonIcon icon={checkmarkCircle} />}
                        {guide.title}
                        {guide.status === 'draft' && (
                          <span className="guide-badge guide-badge-draft">
                            {t('guide.draft')}
                          </span>
                        )}
                      </div>
                      {guide.summary && (
                        <div className="guides-card-summary">
                          {guide.summary}
                        </div>
                      )}
                      {guide.tours.length > 0 && (
                        <div className="guides-card-meta">
                          <IonIcon icon={playOutline} /> {guide.tours.length}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </section>
      )}
    </div>
  );
};

export default Guides;
